import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  PageLoader,
  ActionBar,
  ErrorMessage,
  FormBuilder,
  Form,
  ActionButton,
} from "@redriver/cinnamon";
import { useDispatch, useSelector } from "react-redux";
import { UserType } from "features/../../../shared/constants/enums";
import { TablePagination } from "components/pagination";
import { getAgreementsState } from "../selectors";
import {
  getMasterAgreementsDebounced,
  getAgreementScheduleSheetsDebounced,
  getAgreementScheduleSheets,
  setFilters,
  setPagination,
  updateAccountCredit,
  getAccountCredit,
} from "./actions";
import AgreementListItems from "./AgreementComponents/AgreementListItems";
import ClientHeader from "./ClientHeader";
import {
  AgreementSheetFilters,
  AgreementFilterDisplay,
  SearchAgreements,
} from "../AgreementFilter";
import {
  claimExists,
  PermissionCheck,
  UserTypeCheck,
  withCurrentUser,
} from "features/../../../shared/components/auth";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { Grid, GridColumn } from "semantic-ui-react";
import { AddMasterAgreement } from "./AgreementComponents";
import { AddSchedule } from "./ScheduleComponents";
import CRMUpdateToggleForm from "./CRMUpdateToggleForm";
import AccountCreditRequestModal from "./ScheduleComponents/AccountCreditRequestModal";
import { isRealNumber } from "features/Sheets/ManageSheet";
import { FormattedNumericInput } from "features/../../../shared/components/forms";
import { produce } from "immer";

const AgreementsList = ({ sheetId, currentUser, onClientSelect }) => {
  const {
    companyFilters,
    filters,
    pagination,
    agreementsLoading,
    agreementsLoaded,
    totalAgreements,
    scheduleSheets,
    expandedAgreementsAndSchedules,
  } = useSelector(getAgreementsState);

  const { t } = useTranslation("agreements");
  const dispatch = useDispatch();

  // Load agreements on page load, and change to filters or pagination
  useEffect(() => {
    getMasterAgreementsDebounced(
      dispatch,
      { filters, pagination },
      companyFilters,
    );
    // Remove schedule sheets that are no longer in the list.
    // Reload expanded schedules' sheets for schedules still in the list.
  }, [filters, pagination, companyFilters]);

  useEffect(() => {
    dispatch(
      setFilters(
        produce(filters, (draftState) => {
          draftState.notExpired = !currentUser.isInternalUser;
        }),
      ),
    );
  }, []);

  // Reload sheets when filters change
  useEffect(() => {
    getAgreementScheduleSheetsDebounced(dispatch, scheduleSheets, filters);
  }, [filters]);

  const onFiltersChange = (formData) => {
    dispatch(setFilters(formData));
  };

  const onPaginationChange = (pagination) =>
    dispatch(setPagination(pagination));

  const canEditAccountCredit = claimExists(
    Targets.Schedules,
    Actions.UpdateCreditAvailable,
  );

  return (
    <div className="agreements-list">
      <div>
        <ClientHeader />
        <br />
        <Grid columns={4}>
          <GridColumn width={3}>
            <UserTypeCheck restrictTo={UserType.HQ}>
              <PermissionCheck
                target={Targets.Client}
                action={Actions.UpdateCRMRecord}
              >
                <CRMUpdateToggleForm />
              </PermissionCheck>
            </UserTypeCheck>
          </GridColumn>
          <GridColumn className="right-align" width={4}>
            <PermissionCheck
              target={Targets.Agreements}
              action={Actions.Create}
            >
              <AddMasterAgreement />
            </PermissionCheck>
          </GridColumn>
          <GridColumn width={4}>
            <PermissionCheck target={Targets.Schedules} action={Actions.Create}>
              <AddSchedule />
            </PermissionCheck>
          </GridColumn>
          <GridColumn width={5}>
            <FormBuilder
              loadAction={getAccountCredit}
              loadParams={companyFilters.client.id}
              submitAction={updateAccountCredit}
              submitParams={companyFilters.client.id}
              onSubmitted={() => {
                // Refresh Sheet rows for expanded schedules to ensure actions are enabled or disabled according to the Account Credit
                Object.keys(scheduleSheets)
                  .filter((scheduleId) =>
                    expandedAgreementsAndSchedules.some((e) => e == scheduleId),
                  )
                  .forEach((scheduleId) =>
                    dispatch(getAgreementScheduleSheets(scheduleId, filters)),
                  );
              }}
              renderForm={(formProps, state, formEvents) => (
                <div className="account-credit-container">
                  <Form {...formProps} className="account-credit-form">
                    <FormattedNumericInput
                      field="accountCredit"
                      canBeNull
                      subLabel={process.env.MAJOR_CURRENCY}
                      subLabelPosition="left"
                      placeholder={t("AccountCredit.Header")}
                      disabled={canEditAccountCredit}
                      decimalPlaces={2}
                      className="account-credit-input"
                    />
                    <div className="account-credit-button-group">
                      <ActionButton
                        type="save"
                        onClick={() => formEvents.onSubmit()}
                        loading={state.submitting}
                        className={
                          "action-button" + (state.dirty ? " " : " hidden")
                        }
                      />
                      <ActionButton
                        type="cancel"
                        onClick={() => formEvents.onReset()}
                        loading={state.submitting}
                        className={
                          "action-button" + (state.dirty ? " " : " hidden")
                        }
                      />
                    </div>
                  </Form>

                  <AccountCreditRequestModal
                    clientId={companyFilters.client.id}
                    disabled={isRealNumber(formProps.value.accountCredit)}
                  />
                </div>
              )}
            />
          </GridColumn>
        </Grid>

        <div className="content-container agreements-list">
          <FormBuilder
            value={filters}
            onChange={onFiltersChange}
            renderForm={(formProps) => (
              <ActionBar>
                <ActionBar.Item fluid className="flex search-filter-display">
                  <SearchAgreements formProps={formProps} />
                  <AgreementFilterDisplay formProps={formProps} />
                </ActionBar.Item>
                <PermissionCheck
                  target={Targets.Agreements}
                  action={Actions.Create}
                >
                  <ActionBar.Item></ActionBar.Item>
                </PermissionCheck>
                <div className="agreement-filter-button">
                  <ActionBar.Item>
                    <AgreementSheetFilters formProps={formProps} />
                  </ActionBar.Item>
                </div>
              </ActionBar>
            )}
          />
          {(!agreementsLoaded || agreementsLoading) && <PageLoader />}
          <AgreementListItems
            history={history}
            selectedSheetId={sheetId}
            onClientSelect={onClientSelect}
          />
          <TablePagination
            pageNumber={pagination.pageNumber}
            onChange={onPaginationChange}
            pageSize={pagination.pageSize}
            totalItems={totalAgreements}
          />
          {agreementsLoaded && (
            <ErrorMessage
              header={t("No_data_returned")}
              error={
                !agreementsLoading && totalAgreements == 0
                  ? t("Update_selection")
                  : ""
              }
              omitCodes
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withCurrentUser(AgreementsList);
